import "./Asset/css/global.scss"
import "./DeliveryGuarantee.scss"

import { useMount } from "@channelwill/hooks"
import { Button, Layout, Page, Text, Tooltip } from "@shopify/polaris"
import { EmailIcon } from "@shopify/polaris-icons"
import dayjs from "dayjs"
import { Observer, observer } from "mobx-react"
import { useMemo } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom"

import { Container } from "@/component"
import DatePicker from "@/component/DatePicker"
import Tabs from "@/component/Tabs/index"
import { useSearchParam } from "@/hooks/useSearchParams"
import EarnCommissionBadge from "@/pages/DeliveryGuarantee/Asset/img/earn-commission-badge.png"
import WaiveSubscriptionBadge from "@/pages/DeliveryGuarantee/Asset/img/waive-subscription-badge.png"
import { BagDollarSvg } from "@/pages/DeliveryGuarantee/Asset/svg/BagDollarSvg"
import { DateOption } from "@/pages/Orders/components"
import stores from "@/stores"

import { GetInTouch } from "./components/GetInTouch"
import { useLeaveContactModal } from "./DGSetting/components/LeaveContectModal"

function DeliveryGuarantee() {
  const { t } = useTranslation(["delivery_guarantee", "orders"])
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const isApplied = stores.DeliveryGuarantee.infoData.isFirstApply === 1
  // 已完成授权任务可配置界面
  const { canEnableWidget } = stores.DeliveryGuarantee

  const tabList = useMemo(() => {
    return [
      {
        content: t("delivery_guarantee:General"),
        id: "/protection/general",
        keyword: "general",
      },
      {
        content: t("delivery_guarantee:Covers"),
        id: "/protection/covers",
        keyword: "covers",
      },
      {
        content: t("delivery_guarantee:Claims"),
        id: "/protection/claims",
        keyword: "claims",
      },
      {
        content: t("delivery_guarantee:Bills"),
        id: "/protection/bills",
        keyword: "bills",
      },
    ]
  }, [t])

  const handleSelectChange = (
    selectedValue: string,
    startDate: string | Date | undefined,
    endDate: string | Date | undefined,
  ) => {
    if (tabIndex === 0 && isApplied) {
      // @ts-ignore
      stores.DeliveryGuarantee.getOverviewData(
        String(selectedValue),
        dayjs(startDate).format("YYYY-MM-DD"),
        dayjs(endDate).format("YYYY-MM-DD"),
      )
    }
  }
  const {
    modalComponent: LeaveContactModal,
    showModal: showLeaveContactModal,
  } = useLeaveContactModal()

  const tabIndex = useMemo(() => {
    const targetPageIndex = tabList.findIndex((tab) =>
      pathname.includes(tab.keyword),
    )

    return targetPageIndex < 0 ? 0 : targetPageIndex
  }, [pathname, tabList])

  return (
    <Container
      maxWidth={"1036px"}
      id="pp-delivery-guarantee-page"
      className={"mt-0 DeliveryGuarantee-Page"}
      fullWidth={false}
    >
      <Page
        // @ts-ignore
        maxWidth={"1096px"}
        title={
          (
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div>{t("delivery_guarantee:Sett.DeliveryGuarantee")}</div>
              <div className={"h-[28px] flex gap-1 md:gap-2"}>
                <img
                  src={EarnCommissionBadge}
                  alt="Earn Commission"
                  className="h-[28px]"
                />
                <img
                  src={WaiveSubscriptionBadge}
                  alt="Waive Subscription Fee"
                  className="h-[28px]"
                />
              </div>
            </div>
          ) as any
        }
        primaryAction={
          <div className={"leading-7"}>
            <Tooltip
              hasUnderline
              content={
                <div className="reset-tooltip-max-width">
                  <Text as="p">
                    {t("delivery_guarantee:Sett.ParcelWILLSGotYourBack")}
                  </Text>
                  <Text as="p">
                    <Trans
                      ns={"delivery_guarantee"}
                      i18nKey={"Sett.InsuranceRegulatorsWarn"}
                    ></Trans>
                    <Trans ns="delivery_guarantee" i18nKey="Sett.SourceNAIC">
                      <a
                        className="Polaris-Link Polaris-Link--removeUnderline"
                        onMouseDown={() => {
                          window.open(
                            "https://content.naic.org/article/consumer-alert-beware-package-protection-insurance-scams",
                            "_blank",
                          )
                        }}
                      >
                        NAIC
                      </a>
                    </Trans>
                  </Text>
                </div>
              }
            >
              <Text as={"span"} tone="subdued" breakWord>
                {t(
                  "delivery_guarantee:Sett.UnderwrittenByALicensedUSInsuranceProvider",
                )}
              </Text>
            </Tooltip>
          </div>
        }
      >
        <Layout>
          <Layout.Section>
            <div className="block justify-between sm:flex DeliveryGuarantee-Header">
              <div className="flex-1">
                <Tabs
                  tabs={tabList}
                  activeIndex={tabIndex}
                  onChange={(index: number) => {
                    navigate(tabList[index].id || "/protection")
                    // setDataTimeSelected(DateOption.PastQuarter)
                  }}
                />
              </div>
              <Observer>
                {() => (
                  <div className={"flex justify-start sm:justify-end gap-2 mt-2 sm:mt-0"}>
                    {tabIndex === 0 && isApplied && (
                      <DatePicker
                        preferredAlignment={"right"}
                        buttonProps={{ variant: "secondary" }}
                        onApply={(
                          selected: number,
                          startDate: Date,
                          endDate: Date,
                        ) => {
                          const value = [
                            [0, DateOption.Custom],
                            [1, DateOption.Today],
                            [7, DateOption.PastWeek],
                            [30, DateOption.PastMonth],
                            [60, DateOption.PastTwoMonths],
                            [90, DateOption.PastQuarter],
                            [120, DateOption.PastFourMonths],
                          ].find((v) => {
                            return v[0] === selected
                          })

                          if (value) {
                            handleSelectChange(
                              String(value[1]),
                              startDate,
                              endDate,
                            )
                          }
                        }}
                      />
                    )}
                    {/* GetIntouch */}
                    {tabIndex === 0 && <GetInTouch />}

                  </div>
                )}
              </Observer>
            </div>
          </Layout.Section>
          <Layout.Section>
            <Outlet />
          </Layout.Section>
          {LeaveContactModal}
        </Layout>
      </Page>
    </Container>
  )
}

export default observer(DeliveryGuarantee)
